import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import { ArrowLeft, Plus, Key, Copy, Trash2, BarChart3, Loader2, Shield, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
const EnterpriseApiKeys = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scope: 'read_only', notes: '' });
  const [saving, setSaving] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const [usageKey, setUsageKey] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/enterprise-api/keys', { params: { include_revoked: true } });
      setKeys(res.data.keys || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/admin/enterprise-api/keys', form);
      setJustCreated(res.data);
      setCreateOpen(false);
      setForm({ name: '', scope: 'read_only', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this key? Existing integrations using it will immediately stop working.')) return;
    try {
      await api.post(`/admin/enterprise-api/keys/${id}/revoke`);
      toast.success('Key revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const viewUsage = async (k) => {
    setUsageKey(k);
    setUsage(null);
    try {
      const res = await api.get(`/admin/enterprise-api/keys/${k.id}/usage`);
      setUsage(res.data);
    } catch (e) {
      toast.error('Failed to load usage');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="enterprise-api-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-600" />
              Enterprise API Keys
            </h1>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="new-api-key-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Key
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <Card className="bg-gradient-to-r from-cyan-50 to-violet-50 border-cyan-200">
          <CardContent className="py-4 text-sm text-gray-700">
            <p className="mb-2">
              <Shield className="w-4 h-4 inline text-cyan-600 mr-1" />
              Enterprise API keys authenticate partners against our <code className="bg-white px-1.5 py-0.5 rounded text-xs">/api/v1/*</code> endpoints (shifts, bookings, talent). Pass the raw token as <code className="bg-white px-1.5 py-0.5 rounded text-xs">Authorization: Bearer ent_…</code>.
            </p>
            <p className="text-xs text-gray-500">Read-only in this phase. R/W scopes will unlock in Phase 3.</p>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : keys.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">No API keys yet.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="api-keys-list">
            {keys.map((k) => {
              const revoked = !!k.revoked_at;
              return (
                <Card key={k.id} className={revoked ? 'opacity-60' : ''}>
                  <CardContent className="py-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{k.name}</span>
                        <Badge variant="outline" className="text-xs">{k.scope}</Badge>
                        {revoked && <Badge className="bg-red-100 text-red-700 text-xs">Revoked</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <code className="bg-gray-100 px-2 py-0.5 rounded">{k.prefix}…</code>
                        <span>·</span>
                        <span>Created {formatDate(k.created_at)}</span>
                        <span>·</span>
                        <span>{k.usage_count} calls</span>
                        {k.last_used_at && <span>· Last used {formatDate(k.last_used_at)}</span>}
                      </div>
                      {k.notes && <p className="text-xs text-gray-600 mt-1">{k.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => viewUsage(k)} data-testid={`usage-${k.id}`}>
                        <BarChart3 className="w-3 h-3 mr-1" /> Usage
                      </Button>
                      {!revoked && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => revoke(k.id)}
                          data-testid={`revoke-${k.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>New API Key</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Generate a new API key. Copy it now — we only show the full value once.
          </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Acme analytics integration"
                data-testid="key-name-input"
              />
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="key-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="read_only">Read-only (recommended)</SelectItem>
                  <SelectItem value="read_write" disabled>Read-write (Phase 3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Internal note about the partner / use case"
                data-testid="key-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={create}
              disabled={saving || !form.name.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="create-key-submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Just-created dialog (shows raw key once) */}
      <Dialog open={!!justCreated} onOpenChange={(v) => !v && setJustCreated(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="key-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Key created
            </DialogTitle>
          <DialogDescription className="text-xs text-amber-600">
            Copy this key now — it will not be shown again.
          </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Copy the token now — we store only a hash and <strong>cannot show it again</strong>.
            </p>
            {justCreated && (
              <div className="bg-gray-900 text-emerald-300 font-mono text-xs p-3 rounded flex items-center gap-2 break-all" data-testid="raw-key-display">
                <span className="flex-1">{justCreated.raw_key}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-gray-800 border-gray-700 text-emerald-300 hover:bg-gray-700"
                  onClick={() => copy(justCreated.raw_key)}
                  data-testid="copy-key-btn"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
            )}
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
              <div className="font-medium mb-1">Usage</div>
              <code className="block">curl {window.location.origin}/api/v1/shifts -H "Authorization: Bearer ent_…"</code>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setJustCreated(null)} className="bg-cyan-500 hover:bg-cyan-600 text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage dialog */}
      <Dialog open={!!usageKey} onOpenChange={(v) => !v && setUsageKey(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="usage-dialog">
          <DialogHeader>
            <DialogTitle>Usage — {usageKey?.name}</DialogTitle>
          <DialogDescription className="sr-only">Usage —</DialogDescription>
          </DialogHeader>
          {!usage ? (
            <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-100 rounded p-3"><div className="text-xl font-bold">{usage.total}</div><div className="text-xs text-gray-600">Total calls</div></div>
                <div className="bg-red-100 rounded p-3"><div className="text-xl font-bold">{usage.errors}</div><div className="text-xs text-red-700">Errors</div></div>
                <div className="bg-emerald-100 rounded p-3 text-xs"><div className="font-medium">Last call</div>{usage.last_call ? new Date(usage.last_call).toLocaleString() : '—'}</div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Recent requests</h4>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {usage.recent.length === 0 && <p className="text-xs text-gray-500 italic">No calls yet.</p>}
                  {usage.recent.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                      <Badge className={r.status_code >= 400 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>{r.method}</Badge>
                      <span className="flex-1 font-mono truncate">{r.path}</span>
                      <span className="text-gray-500">{r.status_code}</span>
                      <span className="text-gray-400">{new Date(r.created_at).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnterpriseApiKeys;
